<template>
  <div class="container">
    <div class="columns box-title is-12">
      <div class="column is-12">
        <h4 class="has-text-left">
          <i class="fal fa-key"></i>
          {{ $t('customerSpecialPins.page_title') }}
        </h4>
        <p class="hint">{{ $t('customerSpecialPins.page_hint') }}</p>
      </div>
    </div>

    <!-- Search -->
    <div class="box">
      <label class="label">{{ $t('customerSpecialPins.search_label') }}:</label>
      <input
          v-model="q"
          class="input"
          type="text"
          inputmode="numeric"
          maxlength="30"
          :placeholder="$t('customerSpecialPins.search_placeholder')"
          @input="onSearchInput"
          @keyup.enter="search"
      />
      <p class="hint">{{ $t('customerSpecialPins.search_hint', {min: minDigits}) }}</p>

      <div v-if="searching" class="state-panel small"><Loading/></div>
      <p v-else-if="searchError" class="has-text-danger mt-2">{{ searchError }}</p>
      <p v-else-if="searched && !results.length" class="has-text-grey mt-2">{{ $t('customerSpecialPins.no_customers') }}</p>
      <table v-else-if="results.length" class="table is-fullwidth is-hoverable mt-2">
        <thead>
        <tr>
          <th>{{ $t('customerSpecialPins.columns.name') }}</th>
          <th>{{ $t('customerSpecialPins.columns.tel') }}</th>
          <th>{{ $t('customerSpecialPins.columns.live_pins') }}</th>
          <th></th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="c in results" :key="c.id" :class="{'is-selected-row': selectedId === c.id}">
          <td>{{ c.name || placeholder }}</td>
          <td>{{ c.tel || placeholder }}</td>
          <td>{{ c.live_special_pins }}</td>
          <td class="has-text-right">
            <button class="button is-small" @click="selectCustomer(c.id)">
              {{ $t('customerSpecialPins.open') }}
            </button>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- Selected customer -->
    <div v-if="selectedId" class="box">
      <div v-if="detailLoading" class="state-panel"><Loading/></div>

      <div v-else-if="detailError" class="state-panel error-panel">
        <p>{{ detailError }}</p>
        <button class="button is-small" @click="loadDetail">{{ $t('customerSpecialPins.retry') }}</button>
      </div>

      <template v-else-if="detail">
        <div class="customer-head">
          <div>
            <h5>{{ detail.customer.name || placeholder }}</h5>
            <p>{{ detail.customer.tel || placeholder }}</p>
          </div>
          <span class="tag is-medium" :class="atLimit ? 'is-danger is-light' : 'is-info is-light'">
            {{ $t('customerSpecialPins.live_count', {live: detail.customer.live_special_pins, max: detail.customer.max_special_pins}) }}
          </span>
        </div>

        <div class="add-row">
          <div class="add-field">
            <label class="label">{{ $t('customerSpecialPins.label_label') }}:</label>
            <input
                v-model="label"
                name="label"
                class="input"
                type="text"
                maxlength="100"
                :placeholder="$t('customerSpecialPins.label_placeholder')"
                :disabled="atLimit || issuing"
                @keyup.enter="issue"
            />
            <p class="has-text-danger is-size-7">{{ labelError }}</p>
          </div>
          <button class="button add-button" :class="{'is-loading': issuing}" :disabled="atLimit || issuing" @click="issue">
            <i class="fal fa-plus-circle"></i>&nbsp;{{ $t('customerSpecialPins.add_button') }}
          </button>
        </div>
        <p v-if="atLimit" class="hint has-text-danger">{{ $t('customerSpecialPins.limit_hint', {max: detail.customer.max_special_pins}) }}</p>
        <p class="hint">{{ $t('customerSpecialPins.rule_hint') }}</p>

        <p v-if="!detail.special_pins.length" class="has-text-grey has-text-centered py-3">{{ $t('customerSpecialPins.no_pins') }}</p>
        <div v-else class="table-container">
          <table class="table is-fullwidth is-striped">
            <thead>
            <tr>
              <th>{{ $t('customerSpecialPins.columns.label') }}</th>
              <th>{{ $t('customerSpecialPins.columns.status') }}</th>
              <th>{{ $t('customerSpecialPins.columns.created') }}</th>
              <th>{{ $t('customerSpecialPins.columns.filed') }}</th>
              <th>{{ $t('customerSpecialPins.columns.cancelled') }}</th>
              <th>{{ $t('customerSpecialPins.columns.last_used') }}</th>
              <th>{{ $t('customerSpecialPins.columns.removed') }}</th>
              <th></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="p in detail.special_pins" :key="p.id" :class="{'is-removed': !p.is_active}">
              <td>{{ p.label }}</td>
              <td>
                <span class="tag" :class="p.is_active ? 'is-success is-light' : 'is-light'">
                  {{ p.is_active ? $t('customerSpecialPins.status.active') : $t('customerSpecialPins.status.removed') }}
                </span>
              </td>
              <td>{{ formatDateTime(p.created_at) }}<br><small>{{ personName(p.created_by) }}</small></td>
              <td>{{ p.filed_count }}</td>
              <td>{{ p.cancelled_count }}</td>
              <td>{{ formatDateTime(p.last_used_at) }}</td>
              <td>
                <template v-if="!p.is_active">{{ formatDateTime(p.removed_at) }}<br><small>{{ personName(p.removed_by) }}</small></template>
                <template v-else>{{ placeholder }}</template>
              </td>
              <td class="has-text-right">
                <button v-if="p.is_active" class="button is-small remove-button" @click="askRemove(p)">
                  <i class="fal fa-trash-alt"></i>&nbsp;{{ $t('customerSpecialPins.remove') }}
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>

    <!-- The PIN, shown once -->
    <vs-dialog v-model="issuedOpen" prevent-close not-close>
      <div v-if="issued" class="card-form">
        <h5 class="dialog-title">{{ $t('customerSpecialPins.issued_title') }}</h5>
        <p>{{ issued.customerName || placeholder }} · {{ issued.customerTel || placeholder }}</p>
        <p class="has-text-grey">{{ issued.label }}</p>
        <div class="pin-box">{{ issued.pin }}</div>
        <p class="has-text-danger once-warning">{{ $t('customerSpecialPins.issued_once') }}</p>
        <div class="dialog-actions">
          <button class="button copy-button" @click="copyPin">
            <i class="fal fa-copy"></i>&nbsp;{{ $t('customerSpecialPins.copy') }}
          </button>
          <button class="button" @click="closeIssued">{{ $t('customerSpecialPins.issued_done') }}</button>
        </div>
      </div>
    </vs-dialog>

    <!-- Remove confirm -->
    <vs-dialog v-model="removeOpen" prevent-close>
      <div v-if="removeTarget" class="card-form">
        <h5 class="dialog-title">{{ $t('customerSpecialPins.remove_title') }}</h5>
        <p>{{ $t('customerSpecialPins.remove_confirm', {label: removeTarget.label}) }}</p>
        <p v-if="removeTarget.isLast" class="hint">{{ $t('customerSpecialPins.remove_last_hint') }}</p>
        <div class="dialog-actions">
          <button class="button" :disabled="removing" @click="removeOpen = false">{{ $t('customerSpecialPins.cancel') }}</button>
          <button class="button remove-button" :class="{'is-loading': removing}" :disabled="removing" @click="confirmRemove">
            {{ $t('customerSpecialPins.remove') }}
          </button>
        </div>
      </div>
    </vs-dialog>
  </div>
</template>

<script>
import Loading from '../../../Loading';
import {errorMessage, fetchCustomerPins, issuePin, removePin, searchCustomers} from '@/services/customerSpecialPinService';
import {error as notifyError, success as notifySuccess} from '@/Plugins/Notification';

const MIN_DIGITS = 4;
const LABEL_MAX = 100;

export default {
  name: 'CustomerSpecialPins',
  components: {Loading},
  data() {
    return {
      q: '',
      minDigits: MIN_DIGITS,
      results: [],
      searched: false,
      searching: false,
      searchError: '',
      searchRequestId: 0,
      searchTimer: null,
      selectedId: null,
      detail: null,
      detailLoading: false,
      detailError: '',
      detailRequestId: 0,
      label: '',
      clientLabelError: '',
      serverLabelError: '',
      issuing: false,
      issued: null,
      issuedOpen: false,
      removeTarget: null,
      removeOpen: false,
      removing: false,
      alive: true,
    };
  },
  computed: {
    placeholder() {
      return '—';
    },
    atLimit() {
      return !!this.detail && this.detail.customer.live_special_pins >= this.detail.customer.max_special_pins;
    },
    labelError() {
      return this.clientLabelError || this.serverLabelError;
    },
  },
  watch: {
    // The dialog's own close paths (Esc) must drop the PIN too, not only the ປິດ button.
    issuedOpen(open) {
      if (!open) this.issued = null;
    },
    label() {
      this.clientLabelError = '';
      this.serverLabelError = '';
    },
  },
  methods: {
    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.search, 350);
    },
    async search() {
      clearTimeout(this.searchTimer);
      const q = (this.q || '').trim();
      const requestId = ++this.searchRequestId;
      this.searchError = '';
      if (q.replace(/\D/g, '').length < MIN_DIGITS) {
        this.results = [];
        this.searched = false;
        this.searching = false;
        return;
      }
      this.searching = true;
      try {
        const rows = await searchCustomers(q);
        if (requestId !== this.searchRequestId || !this.alive) return;
        this.results = rows;
        this.searched = true;
      } catch (e) {
        if (requestId !== this.searchRequestId || !this.alive) return;
        this.results = [];
        this.searchError = errorMessage(e, this.$t('customerSpecialPins.error_loading'));
      } finally {
        if (requestId === this.searchRequestId && this.alive) this.searching = false;
      }
    },
    selectCustomer(id) {
      if (String(this.$route.query.user || '') !== String(id)) {
        this.$router.replace({query: {...this.$route.query, user: String(id)}}).catch(() => {});
      }
      this.open(id);
    },
    open(id) {
      this.selectedId = id;
      this.label = '';
      this.clientLabelError = '';
      this.serverLabelError = '';
      this.loadDetail();
    },
    async loadDetail() {
      const id = this.selectedId;
      if (!id) return;
      const requestId = ++this.detailRequestId;
      this.detailLoading = true;
      this.detailError = '';
      try {
        const data = await fetchCustomerPins(id);
        if (requestId !== this.detailRequestId || !this.alive) return;
        this.detail = data;
        this.syncResultCount(data);
      } catch (e) {
        if (requestId !== this.detailRequestId || !this.alive) return;
        this.detail = null;
        this.detailError = errorMessage(e, this.$t('customerSpecialPins.error_loading'));
      } finally {
        if (requestId === this.detailRequestId && this.alive) this.detailLoading = false;
      }
    },
    // Keep the search row's count in step with the detail the admin just changed.
    syncResultCount(data) {
      const row = data && this.results.find((r) => r.id === data.customer.id);
      if (row) row.live_special_pins = data.customer.live_special_pins;
    },
    // After a create/remove — even a failed one, which may still have saved — re-read what the server holds:
    // the open customer's detail, or the search rows when the admin has moved on to someone else.
    refreshAfterChange(customerId) {
      if (this.selectedId === customerId) {
        this.loadDetail();
      } else if (this.results.some((r) => r.id === customerId)) {
        this.search();
      }
    },
    async issue() {
      if (this.issuing || this.atLimit || !this.detail) return;
      const label = (this.label || '').trim();
      // Same rule as the server (required after trim, ≤ 100 characters), in the page's own language.
      if (!label || label.length > LABEL_MAX) {
        this.clientLabelError = this.$t(label ? 'customerSpecialPins.label_too_long' : 'customerSpecialPins.label_required', {max: LABEL_MAX});
        return;
      }

      const customer = this.detail.customer;
      this.issuing = true;
      try {
        const data = await issuePin(customer.id, label);
        if (!this.alive) return;
        this.issued = {
          pin: data.pin,
          label: data.special_pin ? data.special_pin.label : label,
          customerName: customer.name,
          customerTel: customer.tel,
        };
        this.issuedOpen = true;
        this.label = '';
        this.refreshAfterChange(customer.id);
      } catch (e) {
        if (!this.alive) return;
        const data = e && e.response && e.response.data;
        if (data && data.errors && data.errors.label) {
          this.serverLabelError = data.errors.label[0];
        } else {
          this.notify('danger', errorMessage(e, this.$t('customerSpecialPins.error_issue')));
          this.refreshAfterChange(customer.id);
        }
      } finally {
        if (this.alive) this.issuing = false;
      }
    },
    closeIssued() {
      this.issuedOpen = false;
    },
    copyPin() {
      const text = this.issued ? String(this.issued.pin) : '';
      if (!text) return;
      const done = () => this.notify('success', this.$t('customerSpecialPins.copied'));
      const fail = () => this.notify('danger', this.$t('customerSpecialPins.copy_failed'));
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(() => (this.copyFallback(text) ? done() : fail()));
        return;
      }
      this.copyFallback(text) ? done() : fail();
    },
    // http:// origins (no secure context) have no navigator.clipboard — a hidden textarea + execCommand still works there.
    copyFallback(text) {
      try {
        const area = document.createElement('textarea');
        area.value = text;
        area.setAttribute('readonly', '');
        area.style.position = 'fixed';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(area);
        return ok;
      } catch (e) {
        return false;
      }
    },
    askRemove(pin) {
      const live = this.detail ? this.detail.special_pins.filter((p) => p.is_active).length : 0;
      this.removeTarget = {id: pin.id, label: pin.label, customerId: this.detail.customer.id, isLast: live === 1};
      this.removeOpen = true;
    },
    async confirmRemove() {
      if (!this.removeTarget || this.removing) return;
      const target = this.removeTarget;
      this.removing = true;
      try {
        await removePin(target.customerId, target.id);
        if (!this.alive) return;
        this.notify('success', this.$t('customerSpecialPins.removed'));
      } catch (e) {
        if (!this.alive) return;
        this.notify('danger', errorMessage(e, this.$t('customerSpecialPins.error_remove')));
      } finally {
        if (this.alive) {
          this.removing = false;
          this.removeOpen = false;
          this.removeTarget = null;
          this.refreshAfterChange(target.customerId);
        }
      }
    },
    personName(person) {
      return person ? (person.name || `#${person.id}`) : this.placeholder;
    },
    formatDateTime(value) {
      return value ? this.moment(value).format('DD/MM/YYYY HH:mm') : this.placeholder;
    },
    notify(type, message) {
      (type === 'success' ? notifySuccess : notifyError)({message});
    },
  },
  created() {
    const id = Number(this.$route.query.user);
    if (Number.isInteger(id) && id > 0) this.open(id);
  },
  beforeDestroy() {
    this.alive = false;
    this.issued = null;
    clearTimeout(this.searchTimer);
  },
};
</script>

<style lang="scss" scoped>
.container {
  margin-top: 40px;
}

.hint {
  font-size: 12px;
  color: #777;
  margin-top: 4px;
}

// The app's global styles centre these; this page reads left to right.
.label,
.box .hint {
  text-align: left;
}

.mt-2 {
  margin-top: 12px;
}

.py-3 {
  padding: 1.5rem 0;
}

.state-panel {
  min-height: 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #555;

  &.small {
    min-height: 80px;
  }
}

.error-panel {
  color: #b42318;
}

.is-selected-row {
  background: #eef4ff;
}

.customer-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;

  h5 {
    font-size: 18px;
    font-weight: 600;
  }
}

.add-row {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 12px;

  .add-field {
    flex: 1 1 280px;
  }

  .add-button {
    margin-top: 30px;
  }
}

.is-removed td {
  color: #999;
}

.remove-button {
  background: #e74c3c;
  color: #fff;

  &:hover {
    background: #c0392b;
  }
}

.copy-button {
  background: #1abc9c;
  color: #fff;

  &:hover {
    background: #159b81;
  }
}

.card-form {
  width: 420px;
  max-width: 100%;
  text-align: center;
}

.dialog-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
}

.pin-box {
  font-family: monospace;
  font-size: 40px;
  letter-spacing: 10px;
  font-weight: 700;
  margin: 16px 0;
  padding: 12px;
  background: #f5f7fb;
  border-radius: 6px;
}

.once-warning {
  font-size: 13px;
}

.dialog-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 16px;
}
</style>
